import {
  TabList,
  SimpleGridProps,
  Box,
  Card,
  CardBody,
  CardHeader,
  Container,
  SimpleGrid,
  Stack,
  Skeleton,
  TabPanels,
  PageTabs,
  TabPanel
} from '~/design_system'
import { useEffect } from 'react'
import { useAccountMeQuery } from '~/api/accounts'
import { links } from '~/common/links'
import { useHomeConfigQuery, useHomeQuickActionsQuery } from '~/api/home'
import { useMsg } from '~/common/localizationUtils'
import { ContactUsDrawer } from '~/components/v2/ContactUsDrawer'
import { IconTab, IconTabs, IconTabIndicator } from '~/components/v2/IconTabs'
import { Dashboards } from '~/home/Dashboards'
import { FavoriteReports } from '~/home/FavoriteReports'
import { HomeStatCards } from '~/home/HomeStatCards'
import { Messages } from '~/home/Messages'
import { Notifications } from '~/home/Notifications'
import { QuickActions } from '~/home/QuickActions'
import { Bell, MessageBubble } from '~/icons'
import { HomeTaskWidget } from '~/user_tasks/HomeTaskWidget'
import { SadStates } from '~/components/SadStates'
import { ContainerProps } from '~/design_system/chakra'
import { BannerGraphic } from './BannerGraphic'

// We need zIndex 0 here so tooltips can go on top, because HomeStatCards is doing some zIndex stuff to make the
// colored gradient banner work correctly.
const UNDERNEATH_TOOLTIPS_ZINDEX = 0

export const HomeApp = () => {
  const { isCandidateRole, isLoading: isAccountLoading } = useAccountMeQuery()

  useEffect(() => {
    if (!isAccountLoading && isCandidateRole) {
      // replace() so the back button doesn't land on /home/all again (VNDLY-66271)
      window.location.replace(links.candidateTimesheets.href)
    }
  }, [isAccountLoading, isCandidateRole])

  // Candidates can't access the homepage, bounce them to their timesheets landing page (VNDLY-66271).
  if (isAccountLoading || isCandidateRole) return null

  return (
    <Box pos="relative" zIndex={UNDERNEATH_TOOLTIPS_ZINDEX}>
      <BannerGraphic data-testid="banner-graphic-container">
        <Container>
          <HomeStatCards />
        </Container>
      </BannerGraphic>
      <GridLayoutContainer data-testid="grid-container" />
      <ContactUsDrawer />
    </Box>
  )
}

const GridLayoutContainer = (props: ContainerProps) => {
  const {
    data: quickActions,
    isLoading: quickActionsIsLoading,
    isError: quickActionsIsError
  } = useHomeQuickActionsQuery()
  const quickActionsCount = quickActions?.length ?? 0

  return (
    <Container zIndex={1} pos="relative" {...props}>
      {/* When loading, show the default layout */}
      {(quickActionsIsLoading || quickActionsCount > 3) && <QuickActions mb={6} />}
      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6}>
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }} w="100%">
          {/*
           * Quick actions layout when less than or equal to 3
           * When only one quick action -> make it occupy 1/2 of 2 column grid
           * When two quick actions -> make each occupy 1/2 of 2 column grid
           * When three quick actions -> make each occupy 1/3 of 3 column grid
           */}
          {!quickActionsIsLoading && (quickActionsCount <= 3 || quickActionsIsError) && (
            <QuickActions defaultSlidesCount={Math.max(quickActionsCount, quickActionsIsError ? 1 : 2)} />
          )}
          <HomeTaskWidget />
          <Dashboards display={{ base: 'none', xl: 'grid' }} />
        </Stack>
        <RightColumn />
        <Dashboards display={{ base: 'grid', xl: 'none' }} />
      </SimpleGrid>
    </Container>
  )
}

const RightColumn = (props: SimpleGridProps) => {
  const { data, isLoading } = useHomeConfigQuery()
  const msg = useMsg()
  const defaultTabIndex = data?.set_event_tab_as_default ? 2 : 0

  return (
    <SimpleGrid
      columns={{ sm: 1, md: 2, xl: 1 }}
      alignContent="flex-start"
      alignItems="flex-start"
      spacing={6}
      {...props}>
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (
              <Card>
                <CardHeader>
                  <Skeleton w="full" height={4} my={4} />
                </CardHeader>
                <CardBody>
                  <Skeleton height={4} my={4} mx={6} />
                  <Skeleton height={4} my={4} mx={6} />
                  <Skeleton height={4} my={4} mx={6} />
                </CardBody>
              </Card>
            )
          }
        ]}>
        <IconTabs>
          {({ indicators, setTabIndicator }) => (
            <PageTabs defaultIndex={Number(defaultTabIndex)}>
              <TabList borderBottom="1px">
                <IconTab aria-label={msg('notification.card.heading')}>
                  <Bell />
                  <IconTabIndicator indicator={indicators[0]} />
                </IconTab>
                <IconTab aria-label={msg('message.card.label')}>
                  <MessageBubble />
                  <IconTabIndicator indicator={indicators[1]} />
                </IconTab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Notifications setTabIndicator={setTabIndicator(0)} />
                </TabPanel>
                <TabPanel>
                  <Messages setTabIndicator={setTabIndicator(1)} />
                </TabPanel>
              </TabPanels>
            </PageTabs>
          )}
        </IconTabs>
      </SadStates>
      <FavoriteReports />
    </SimpleGrid>
  )
}
